import React, { useMemo, useState, useEffect, ChangeEvent } from "react";
import { BookOpen, TrendingUp } from "lucide-react";
import { useAuth } from "../../../context/AuthContexts";
import Modal from "../../../components/Modal";
import WeekDropdown from "../../../components/WeekDropdown";
import { describeApiError } from "../../../lib/apiError";

import {
  useGetLogbooksQuery,
  useGetWeekEntriesQuery,
  useSaveEntryMutation,
  useSearchSupervisorQuery,
  useRequestSupervisorMutation,
} from "../api/logbookApi";

import {
  Day,
  DAYS,
  entriesKey,
  supervisionStatusKey,
  supervisorNameKey,
  WEEKS,
} from "../utils/logbook";

const DAY_TO_NUM: Record<Day, number> = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
};

const NUM_TO_DAY: Record<number, Day> = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
};

export default function MyLogbook() {
  const { user } = useAuth();

  /** ====================== RTK QUERY CALLS ====================== **/
  const { data: logbooks = [], isLoading, error } = useGetLogbooksQuery();

  const [selectedWeek, setSelectedWeek] = useState(1);
  // `currentData`, NOT `data` — see LogbookDetails. `data` outlives a change of
  // week, so a week the server will not serve left the previous week's entries
  // on screen under the new week's number.
  const { currentData: weekData } = useGetWeekEntriesQuery(selectedWeek, {
    skip: !user,
  });
  const [saveEntry] = useSaveEntryMutation();
  const [sendSupervisorRequest] = useRequestSupervisorMutation();

  /** Supervisor live search (auto call when length >=3) */
  const [supQuery, setSupQuery] = useState("");
  const shouldSearch = supQuery.length >= 3;
  const { data: supResults = [], isFetching: supLoading } =
    useSearchSupervisorQuery(supQuery, { skip: !shouldSearch });

  /** ====================== LOCAL STATES ====================== **/
  const [status, setStatus] = useState<
    "none" | "pending" | "approved" | "rejected"
  >("none");
  const [supervisorName, setSupervisorName] = useState("");

  const [dailyText, setDailyText] = useState<Record<Day, string>>({
    Monday: "",
    Tuesday: "",
    Wednesday: "",
    Thursday: "",
    Friday: "",
  });
  const [dailyDate, setDailyDate] = useState<Record<Day, string>>({
    Monday: "",
    Tuesday: "",
    Wednesday: "",
    Thursday: "",
    Friday: "",
  });
  const [dailyHours, setDailyHours] = useState<Record<Day, string>>({
    Monday: "",
    Tuesday: "",
    Wednesday: "",
    Thursday: "",
    Friday: "",
  });
  const [entryMeta, setEntryMeta] = useState<
    Record<Day, { id?: string; status?: string }>
  >({ Monday: {}, Tuesday: {}, Wednesday: {}, Thursday: {}, Friday: {} });

  const [logbook, setLogbook] = useState<any | null>(null);
  const [modal, setModal] = useState({ open: false, title: "", message: "" });
  const availableWeeks = useMemo(() => WEEKS.slice(0, 52), []);

  /** ====================== LOAD WEEK ENTRIES ====================== **/
  useEffect(() => {
    if (!weekData?.entries) return;

    const txt: any = {},
      dt: any = {},
      hr: any = {},
      meta: any = {};
    DAYS.map((d) => {
      txt[d] = "";
      dt[d] = "";
      hr[d] = "";
      meta[d] = {};
    });

    weekData.entries.forEach((e: any) => {
      const day = NUM_TO_DAY[e.day];
      txt[day] = e.activity || "";
      dt[day] = (e.entry_date || "").slice(0, 10);
      hr[day] = String(e.hours || "");
      meta[day] = { id: e.id, status: e.status };
    });

    setDailyText(txt);
    setDailyDate(dt);
    setDailyHours(hr);
    setEntryMeta(meta);
  }, [weekData]);

  /** ====================== SELECT LOGBOOK ====================== **/
  const handleSelectLogbook = (lb: any) => {
    setLogbook(lb);
    const st = String(lb.status || "").toLowerCase();
    if (st === "pending") setStatus("pending");
    else if (st === "approved" || lb.supervisor) setStatus("approved");
    else if (st === "rejected") setStatus("rejected");
    else setStatus("none");
    setSupervisorName(lb.supervisor?.name || "");
  };

  /** ====================== SAVE A DAY ENTRY ====================== **/
  const handleSaveDay = async (day: Day) => {
    try {
      await saveEntry({
        entry_date: dailyDate[day],
        activity: dailyText[day],
        hours: Number(dailyHours[day]),
        week: selectedWeek,
        day: DAY_TO_NUM[day],
      }).unwrap();

      setModal({
        open: true,
        title: "Saved",
        message: `${day} entry saved successfully.`,
      });
    } catch (e: any) {
      setModal({ open: true, title: "Error", message: "Failed saving entry." });
    }
  };

  /** ====================== REQUEST SUPERVISOR ====================== **/
  const requestSupervisor = async (m: any) => {
    try {
      await sendSupervisorRequest({
        supervisor_id: m.id,
        message: "Requesting supervision",
      }).unwrap();
      setStatus("pending");
      setSupervisorName(m.name);
      setModal({
        open: true,
        title: "Request Sent",
        message: `Request sent to ${m.name}.`,
      });
    } catch (e: any) {
      // The API refuses a supervisor who is behind on dues or has been stood
      // down, and the reason names them and says why. Swallowing it left the
      // candidate with "Failed to send request" and nothing to act on.
      setModal({
        open: true,
        title: "Error",
        message: e?.data?.message || "Failed to send request",
      });
    }
  };

  /** ====================== UI SECTION ====================== **/
  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {/* ------------------------------------------------ LOGBOOK LIST SCREEN ------------------------------------------------ */}
      {!logbook && (
        <div className="bg-white p-6 rounded-xl border">
          <h2 className="text-lg font-bold mb-3">My Logbooks</h2>

          {isLoading && <p>Loading...</p>}
          {error && (
            <p className="text-red-500">{describeApiError(error, "Failed loading Logbooks")}</p>
          )}

          {!isLoading && logbooks.length === 0 && <p>No Logbook found</p>}

          {logbooks.length > 0 && (
            <>
              {/* Desktop / tablet: table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      <th className="px-3 py-2">Level</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Supervisor</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {logbooks.map((lb) => (
                      <tr key={lb.id} className="border-b">
                        <td className="p-3">{lb.stage || "-"}</td>
                        <td className="p-3">{lb.status}</td>
                        <td className="p-3">{lb.supervisor?.name || "—"}</td>
                        <td className="p-3 text-right">
                          <button
                            className="px-3 py-1 border rounded hover:bg-gray-50"
                            onClick={() => handleSelectLogbook(lb)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile: cards */}
              <div className="sm:hidden divide-y">
                {logbooks.map((lb) => (
                  <div key={lb.id} className="py-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">Level {lb.stage || "-"}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{lb.status}</div>
                      </div>
                      <button
                        className="px-3 py-1.5 border rounded-lg text-xs font-semibold hover:bg-gray-50 shrink-0"
                        onClick={() => handleSelectLogbook(lb)}
                      >
                        View
                      </button>
                    </div>
                    <div className="text-xs text-gray-500">
                      <span className="text-gray-400 uppercase tracking-wider text-[10px] font-semibold">Supervisor: </span>
                      {lb.supervisor?.name || "—"}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ------------------------------------------------ LOGBOOK DETAILS ------------------------------------------------ */}
      {logbook && (
        <div className="mt-6 flex flex-col lg:flex-row gap-6">
          {/* LEFT PANEL */}
          <div className="w-full lg:w-80 space-y-4">
            <div className="bg-indigo-50 p-5 rounded-xl border">
              <BookOpen size={24} className="text-indigo-600" />
              <h2 className="text-3xl font-black mt-2">
                Level {logbook.stage || 1}
              </h2>
            </div>

            {status === "approved" && (
              <div className="bg-white p-4 rounded-xl border">
                <h4 className="font-bold text-sm">Select Week</h4>
                <WeekDropdown
                  value={selectedWeek}
                  onChange={setSelectedWeek}
                  weeks={availableWeeks}
                />
              </div>
            )}

            <button
              className="text-xs border px-3 py-1 rounded"
              onClick={() => setLogbook(null)}
            >
              ← Back
            </button>
          </div>

          {/* RIGHT PANEL */}
          <div className="flex-1">
            {status === "none" && (
              <div className="bg-white p-6 rounded-xl border space-y-4">
                <p>Select supervisor to unlock logbook entries</p>
                <input
                  placeholder="Search Supervisor..."
                  value={supQuery}
                  onChange={(e) => setSupQuery(e.target.value)}
                  className="border px-3 py-1 rounded w-full"
                />

                {supLoading && <p className="text-xs">Searching…</p>}

                {supResults.length > 0 && (
                  <div className="bg-gray-50 p-3 rounded">
                    {supResults.slice(0, 10).map((m: any) => (
                      <div
                        key={m.id}
                        className="flex justify-between py-1 border-b"
                      >
                        <span>{m.name}</span>
                        <button
                          className="text-xs text-indigo-600"
                          onClick={() => requestSupervisor(m)}
                        >
                          Request
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {status === "approved" && (
              <div className="space-y-6">
                <h3 className="text-sm font-bold">
                  Week {selectedWeek} Entries
                </h3>

                {DAYS.map((day) => (
                  <div
                    key={day}
                    className="bg-white p-4 rounded-xl border space-y-2"
                  >
                    <p className="font-medium">{day}</p>

                    <textarea
                      rows={3}
                      className="w-full border p-2 rounded"
                      value={dailyText[day]}
                      onChange={(e) =>
                        setDailyText({ ...dailyText, [day]: e.target.value })
                      }
                      placeholder={`Write ${day} log...`}
                    />

                    <div className="flex gap-4">
                      <input
                        type="date"
                        className="border p-2 rounded w-full"
                        value={dailyDate[day]}
                        onChange={(e) =>
                          setDailyDate({ ...dailyDate, [day]: e.target.value })
                        }
                      />
                      <input
                        type="number"
                        className="border p-2 rounded w-40"
                        value={dailyHours[day]}
                        onChange={(e) =>
                          setDailyHours({
                            ...dailyHours,
                            [day]: e.target.value,
                          })
                        }
                        placeholder="Hours"
                      />
                      <button
                        onClick={() => handleSaveDay(day)}
                        className="bg-indigo-600 text-white px-3 rounded text-xs"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal for success/error */}
      <Modal
        open={modal.open}
        title={modal.title}
        onClose={() => setModal({ open: false, title: "", message: "" })}
      >
        {modal.message}
      </Modal>
    </div>
  );
}
